import type { Icon } from 'fifa-gis-library'

type IconFactoryOptions = Icon & IconOptions

export interface IconOptions {
  label?: string
  scale?: number
  fillColor?: string
  strokeWeight?: number
  strokeColor?: string
  fontColor?: string
  withTime?: boolean
  anchorOffsetHorizontal?: number
  anchorOffsetVertical?: number
  width?: number
}

export const defaultIcon = ({
  fillColor = '#5ac9dd',
  label = null,
  scale = 1.0,
  strokeColor = '#0082b5',
  strokeWeight = 2.2,
}: Partial<IconFactoryOptions> = {}) => {
  const width = 30 * scale
  const height = 47 * scale

  return {
    size: [width, height],
    source: `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="-9 -44 18 45">
        <path d="M 0 0 C -2 -20 -10 -22 -10 -30 A 10 10 0 1 1 10 -30 C 10 -22 2 -20 0 0 z M -2 -30 a 2 2 0 1 1 4 0 a 2 2 0 1 1 -4 0 z M -1 -30 a 1 1 0 1 1 2 0 a 1 1 0 1 1 -2 0 z M -3 -30 a 3 3 0 1 1 6 0 a 3 3 0 1 1 -6 0" fill="${fillColor}" stroke-width="${strokeWeight}" stroke="${strokeColor}">
        </path>
      </svg>
    `,
    anchor: [15, 47],
    labelOffset: [15, 15],
    label: label || '',
    labelStyle: {
      fontColor: '#ffffff',
      fontSize: 11,
      fontWeight: 500,
    },
  }
}

export const iconFactory = ({
  fillColor = '#ff4646',
  label = null,
  scale = 1.0,
  strokeColor = '#770000',
  ...rest
}: Partial<IconFactoryOptions> = {}) => {
  const width = 30 * scale
  const height = 47 * scale
  const fontSize = 11 * scale

  return {
    size: [width, height],
    source: `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="-9 -44 18 45">
        <path d="M 0 0 C -2 -20 -10 -22 -10 -30 A 10 10 0 1 1 10 -30 C 10 -22 2 -20 0 0 Z" fill="${fillColor}" stroke-width="1" stroke="${strokeColor}">
        </path>
      </svg>
    `,
    anchor: [15 * scale, 47 * scale],
    labelOffset: [15 * scale, 16 * scale],
    label: label || '',
    labelStyle: {
      fontColor: '#ffffff',
      fontSize: fontSize,
      fontWeight: 500,
    },
    ...rest,
  }
}

export const dropoffIconFactory = ({
  label = null,
  fillColor = '#0d98ff',
  strokeColor = '#FFFFFF',
  scale = 1.2,
  withTime = true,
  anchorOffsetHorizontal = 10,
  anchorOffsetVertical = 13,

  ...rest
}: Partial<IconFactoryOptions> = {}) => {
  const width = 58 * scale
  const height = 30 * scale
  const fontSize = 11 * scale
  const timeRect = `<rect x="3" y="2" width="60" height="20" rx="10" fill="${fillColor}"/>`
  return {
    size: [width, height],
    source: `
        <svg width="${width}" height="${height}" viewBox="0 0 63 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        ${withTime ? timeRect : ''}
        <g filter="url(#filter0_d_79_65)">
        <circle cx="12" cy="12" r="10" fill="${strokeColor}"/>
        <circle cx="12" cy="12" r="9.5" stroke="${fillColor}"/>
        </g>
        <circle cx="12" cy="12" r="6.5" fill="${strokeColor}" stroke="${fillColor}"/>
        <circle cx="1" cy="1" r="1" transform="matrix(1 0 0 -1 11 13)" fill="${fillColor}"/>
        <defs>
        <filter id="filter0_d_79_65" x="0" y="0" width="26" height="26" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dx="1" dy="1"/>
        <feGaussianBlur stdDeviation="1.5"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_79_65"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_79_65" result="shape"/>
        </filter>
        </defs>
        </svg>
    `,
    anchor: [anchorOffsetHorizontal * scale, anchorOffsetVertical * scale],
    labelOffset: [38 * scale, (30 / 2) * scale],
    label: label || '',
    labelStyle: {
      fontColor: '#ffffff',
      fontSize: fontSize,
      fontWeight: 500,
    },
    ...rest,
  }
}

export const pickupIconFactory = ({
  label = null,
  fillColor = '#FFFFFF',
  strokeColor = '#32a85e',
  scale = 1.2,
  withTime = true,
  anchorOffsetVertical = 13,
  anchorOffsetHorizontal = 10,
  ...rest
}: Partial<IconFactoryOptions> = {}) => {
  const width = 58 * scale
  const height = 30 * scale
  const fontSize = 11 * scale

  const timeRect = `<rect x="3.5" y="2.5" width="60" height="19" rx="10" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2"/>`

  return {
    size: [width, height],
    source: `
      <svg width="${width}" height="${height}" viewBox="0 0 64 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      ${withTime ? timeRect : ''}
      <g>
      <circle cx="12" cy="12" r="10" fill="${strokeColor}"/>
      <circle cx="12" cy="12" r="9.5" stroke="${strokeColor}" stroke-width="2"/>
      </g>
      <path d="M18.5 11.134C19.1667 11.5189 19.1667 12.4811 18.5 12.866L9.5 18.0622C8.83333 18.4471 8 17.966 8 17.1962L8 6.80385C8 6.03405 8.83333 5.55292 9.5 5.93782L18.5 11.134Z" fill="${fillColor}"/>
      <defs>
      </defs>
      </svg>

    `,
    anchor: [anchorOffsetHorizontal * scale, anchorOffsetVertical * scale],
    labelOffset: [38 * scale, (30 / 2) * scale],
    label: label || '',
    labelStyle: {
      fontColor: strokeColor,
      fontSize: fontSize,
      fontWeight: 500,
    },
    ...rest,
  }
}
export const waypointIconFactory = ({
  label = null,
  fillColor = '#ffffff',
  strokeColor = '#326295',
  scale = 1.0,
  anchorOffsetVertical = 20,
  anchorOffsetHorizontal = 20,
  ...rest
}: Partial<IconFactoryOptions> = {}) => {
  const width = 40 * scale
  const height = 40 * scale
  const fontSize = 11 * scale

  return {
    size: [width, height],
    source: `
    <svg  width="${width}" height="${height}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" style="filter: drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.3))" cy="12" r="8" fill="${strokeColor}"></circle>
      <circle cx="12" cy="12" r="6" fill="${fillColor}"></circle>
    </svg>
    `,
    anchor: [anchorOffsetHorizontal * scale, anchorOffsetVertical * scale],
    labelOffset: [20 * scale, 22 * scale],
    label: label || '',
    ...rest,
    labelStyle: {
      fontColor: '#326295',
      fontSize: fontSize,
      fontWeight: 500,
      ...(rest.labelStyle || {}),
    },
  }
}

export const widerWaypointIconFactory = ({
  label = null,
  fillColor = '#ffffff',
  fontColor = '#326295',
  strokeColor = '#326295',
  scale = 1.0,
  anchorOffsetHorizontal = 12,
  anchorOffsetVertical = 15,
  width: iconWidth = 30,
  ...rest
}: Partial<IconFactoryOptions> = {}) => {
  const width = iconWidth * scale
  const height = 26 * scale
  const fontSize = 11 * scale

  return {
    size: [width, height],
    source: `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="${width}" height="${height}" rx="14" fill="${strokeColor}"/>
      <rect x="4" y="3" width="${width - 8 * scale}" height="${
      height - 6 * scale
    }" rx="11" fill="${fillColor}"/>
    </svg>
    `,
    anchor: [anchorOffsetHorizontal * scale, anchorOffsetVertical * scale],
    labelOffset: [(iconWidth / 2) * scale, 14 * scale],
    label: label || '',
    labelStyle: {
      fontColor: fontColor,
      fontSize: fontSize,
      fontWeight: 500,
    },
    ...rest,
  }
}
